import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import ModalPortal from './ModalPortal';
import { Config } from '../../services/config';
import { getToken } from '../../services/config';
import './ImportModal.css';

// =============================================
// TIPOS
// =============================================

export interface ImportColumnConfig {
  header: string;       // Nome da coluna em PT-BR (como aparece no Excel)
  field: string;        // Nome do campo no banco de dados
  required: boolean;    // Se o campo é obrigatório
  type: 'string' | 'number' | 'date' | 'boolean' | 'decimal';
  example: string;      // Exemplo para a planilha modelo
}

export interface ImportConfig {
  entityName: string;           // Nome da entidade para display
  entityKey: string;            // Chave da entidade para API (ex: 'obras', 'veiculos')
  columns: ImportColumnConfig[];
  exampleRows: Record<string, string>[];  // 3 rows de exemplo
}

interface ImportResult {
  linha: number;
  sucesso: boolean;
  mensagem: string;
  dados: Record<string, any>;
  erros: string[];
}

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  config: ImportConfig;
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

const ImportModal: React.FC<ImportModalProps> = ({ isOpen, onClose, onImportComplete, config }) => {
  const [step, setStep] = useState<'upload' | 'preview' | 'results'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<Record<string, any>[]>([]);
  const [results, setResults] = useState<ImportResult[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [reprocessingRow, setReprocessingRow] = useState<number | null>(null);
  const [isReprocessingAll, setIsReprocessingAll] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // =============================================
  // GERAR PLANILHA MODELO
  // =============================================

  const downloadTemplate = useCallback(() => {
    const wb = XLSX.utils.book_new();

    // Criar headers
    const headers = config.columns.map(col =>
      col.required ? `${col.header}*` : col.header
    );

    // Criar linhas de exemplo
    const exampleData = config.exampleRows.map(row => {
      return config.columns.map(col => {
        const key = col.required ? `${col.header}*` : col.header;
        return row[key] || row[col.header] || '';
      });
    });

    const wsData = [headers, ...exampleData];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Ajustar largura das colunas
    const colWidths = headers.map((h, i) => {
      const maxLen = Math.max(
        h.length,
        ...exampleData.map(row => String(row[i] || '').length)
      );
      return { wch: Math.min(Math.max(maxLen + 2, 12), 35) };
    });
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, config.entityName);
    XLSX.writeFile(wb, `modelo_importacao_${config.entityKey}.xlsx`);
  }, [config]);

  // =============================================
  // UPLOAD E PARSE DO ARQUIVO
  // =============================================

  const handleFile = useCallback((selectedFile: File) => {
    if (!selectedFile) return;

    const isValidType = selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls');

    if (!isValidType) {
      alert('Por favor, selecione um arquivo Excel (.xlsx ou .xls)');
      return;
    }

    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });

        if (jsonData.length === 0) {
          alert('A planilha está vazia. Por favor, adicione dados e tente novamente.');
          return;
        }

        setParsedData(jsonData as Record<string, any>[]);
        setStep('preview');
      } catch (error) {
        alert('Erro ao ler o arquivo. Verifique se é um arquivo Excel válido.');
        console.error('Erro ao parsear Excel:', error);
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  // =============================================
  // IMPORTAÇÃO
  // =============================================

  const handleImport = async () => {
    if (!file) return;

    setIsImporting(true);
    setStep('results');

    try {
      const formData = new FormData();
      formData.append('arquivo', file);

      const token = getToken();
      const response = await fetch(
        `${Config.API_BASE_URL}/importar/${config.entityKey}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      const result = await response.json();

      if (result.success || result.sucesso) {
        const resultados = result.resultados || result.data?.resultados || result.dados?.resultados || [];
        setResults(resultados);
      } else if (result.resultados && result.resultados.length > 0) {
        setResults(result.resultados);
      } else {
        alert(result.message || result.mensagem || 'Erro ao importar planilha');
        setStep('preview');
      }
    } catch (error) {
      console.error('Erro ao importar:', error);
      alert('Erro de conexão ao importar planilha. Tente novamente.');
      setStep('preview');
    } finally {
      setIsImporting(false);
    }
  };

  // =============================================
  // EDIÇÃO E REPROCESSAMENTO DE LINHA
  // =============================================

  const startEditing = (rowIndex: number) => {
    setEditingRow(rowIndex);
    setEditData({ ...results[rowIndex].dados });
  };

  const cancelEditing = () => {
    setEditingRow(null);
    setEditData({});
  };

  const handleEditChange = (field: string, value: string) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, rowIndex: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      reprocessRow(rowIndex);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    }
  };

  const reprocessSingleRow = async (rowIndex: number, dataToSend: Record<string, any>): Promise<boolean> => {
    const token = getToken();
    const response = await fetch(
      `${Config.API_BASE_URL}/importar/${config.entityKey}/reprocessar`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ dados: dataToSend })
      }
    );

    const result = await response.json();

    setResults(prev => {
      const updated = [...prev];
      if (result.success || result.sucesso) {
        updated[rowIndex] = {
          ...updated[rowIndex],
          sucesso: true,
          mensagem: 'Importado com sucesso!',
          erros: [],
          dados: dataToSend
        };
      } else {
        const erros = result.erros || result.data?.erros || result.dados?.erros || [result.message || result.mensagem || 'Erro ao reprocessar'];
        updated[rowIndex] = {
          ...updated[rowIndex],
          sucesso: false,
          mensagem: erros.join('; '),
          erros: erros,
          dados: dataToSend
        };
      }
      return updated;
    });

    return result.success || result.sucesso || false;
  };

  const reprocessRow = async (rowIndex: number) => {
    setReprocessingRow(rowIndex);

    try {
      const dataToSend = editingRow === rowIndex ? { ...editData } : { ...results[rowIndex].dados };
      await reprocessSingleRow(rowIndex, dataToSend);
      setEditingRow(null);
      setEditData({});
    } catch (error) {
      console.error('Erro ao reprocessar linha:', error);
      alert('Erro de conexão ao reprocessar. Tente novamente.');
    } finally {
      setReprocessingRow(null);
    }
  };

  const reprocessAllErrors = async () => {
    setIsReprocessingAll(true);
    setEditingRow(null);
    setEditData({});

    try {
      // Capturar snapshot dos dados a reprocessar antes do loop
      const rowsToReprocess = results
        .map((r, idx) => ({ idx, dados: { ...r.dados } }))
        .filter((_, idx) => !results[idx].sucesso);

      for (const { idx, dados } of rowsToReprocess) {
        try {
          await reprocessSingleRow(idx, dados);
        } catch (error) {
          console.error(`Erro ao reprocessar linha ${idx}:`, error);
        }
      }
    } finally {
      setIsReprocessingAll(false);
    }
  };

  // =============================================
  // RESET E CLOSE
  // =============================================

  const handleClose = () => {
    const hasImported = results.some(r => r.sucesso);
    setStep('upload');
    setFile(null);
    setParsedData([]);
    setResults([]);
    setEditingRow(null);
    setEditData({});
    onClose();
    if (hasImported) {
      onImportComplete();
    }
  };

  // =============================================
  // HELPERS
  // =============================================

  const getColumnHeaders = (): string[] => {
    if (parsedData.length === 0) return [];
    return Object.keys(parsedData[0]);
  };

  const successCount = results.filter(r => r.sucesso).length;
  const errorCount = results.filter(r => !r.sucesso).length;

  const findColumnHeader = (col: ImportColumnConfig): string => {
    return col.required ? `${col.header}*` : col.header;
  };

  // =============================================
  // RENDER
  // =============================================

  return (
    <ModalPortal isOpen={isOpen}>
      <div className="import-modal__overlay" onClick={handleClose}>
        <div className="import-modal__container" onClick={(e) => e.stopPropagation()}>

          {/* HEADER */}
          <div className="import-modal__header">
            <h2 className="import-modal__title">
              <i className="fas fa-file-excel"></i>
              Importar {config.entityName}
            </h2>
            <button className="import-modal__close" onClick={handleClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* STEP: UPLOAD */}
          {step === 'upload' && (
            <div className="import-modal__body">
              {/* Template Download */}
              <div className="import-modal__template">
                <div className="import-modal__template-info">
                  <i className="fas fa-info-circle"></i>
                  <div>
                    <strong>Baixe o modelo de planilha</strong>
                    <p>Use o modelo abaixo como referência. Colunas com asterisco (*) são obrigatórias.</p>
                  </div>
                </div>
                <button className="import-modal__btn import-modal__btn--template" onClick={downloadTemplate}>
                  <i className="fas fa-download"></i>
                  Baixar Modelo
                </button>
              </div>

              {/* Upload Area */}
              <div
                className={`import-modal__dropzone ${dragActive ? 'import-modal__dropzone--active' : ''}`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => e.target.files && handleFile(e.target.files[0])}
                  style={{ display: 'none' }}
                />
                <i className="fas fa-cloud-upload-alt import-modal__dropzone-icon"></i>
                <p className="import-modal__dropzone-text">
                  Arraste e solte seu arquivo Excel aqui
                </p>
                <p className="import-modal__dropzone-subtext">
                  ou clique para selecionar o arquivo (.xlsx, .xls)
                </p>
              </div>
            </div>
          )}

          {/* STEP: PREVIEW */}
          {step === 'preview' && (
            <div className="import-modal__body">
              <div className="import-modal__preview-info">
                <i className="fas fa-table"></i>
                <span>
                  <strong>{parsedData.length}</strong> registro(s) encontrado(s) no arquivo
                  {file && <> - <em>{file.name}</em></>}
                </span>
              </div>

              <div className="import-modal__table-wrapper">
                <table className="import-modal__table">
                  <thead>
                    <tr>
                      <th className="import-modal__th import-modal__th--num">#</th>
                      {getColumnHeaders().map((header, i) => (
                        <th key={i} className="import-modal__th">{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.slice(0, 100).map((row, rowIdx) => (
                      <tr key={rowIdx} className="import-modal__tr">
                        <td className="import-modal__td import-modal__td--num">{rowIdx + 1}</td>
                        {getColumnHeaders().map((header, colIdx) => (
                          <td key={colIdx} className="import-modal__td">
                            {row[header] instanceof Date
                              ? (row[header] as Date).toLocaleDateString('pt-BR')
                              : String(row[header] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {parsedData.length > 100 && (
                <p className="import-modal__preview-note">
                  Exibindo os primeiros 100 registros de {parsedData.length} total.
                </p>
              )}

              <div className="import-modal__actions">
                <button
                  className="import-modal__btn import-modal__btn--secondary"
                  onClick={() => { setStep('upload'); setFile(null); setParsedData([]); }}
                >
                  <i className="fas fa-arrow-left"></i>
                  Voltar
                </button>
                <button
                  className="import-modal__btn import-modal__btn--primary"
                  onClick={handleImport}
                  disabled={isImporting}
                >
                  {isImporting ? (
                    <>
                      <span className="import-modal__spinner"></span>
                      Importando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload"></i>
                      Importar {parsedData.length} Registro(s)
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* STEP: RESULTS */}
          {step === 'results' && (
            <div className="import-modal__body">
              {isImporting ? (
                <div className="import-modal__loading">
                  <span className="import-modal__spinner import-modal__spinner--large"></span>
                  <p>Processando importação...</p>
                  <p className="import-modal__loading-sub">Aguarde enquanto os registros são importados.</p>
                </div>
              ) : (
                <>
                  {/* Resumo */}
                  <div className="import-modal__summary">
                    <div className="import-modal__summary-item import-modal__summary-item--total">
                      <span className="import-modal__summary-number">{results.length}</span>
                      <span className="import-modal__summary-label">Total</span>
                    </div>
                    <div className="import-modal__summary-item import-modal__summary-item--success">
                      <span className="import-modal__summary-number">{successCount}</span>
                      <span className="import-modal__summary-label">Importados</span>
                    </div>
                    <div className="import-modal__summary-item import-modal__summary-item--error">
                      <span className="import-modal__summary-number">{errorCount}</span>
                      <span className="import-modal__summary-label">Com Erro</span>
                    </div>
                  </div>

                  {/* Botão Reprocessar Todos com Erro */}
                  {errorCount > 0 && (
                    <div className="import-modal__reprocess-all">
                      <button
                        className="import-modal__btn import-modal__btn--reprocess-all"
                        onClick={reprocessAllErrors}
                        disabled={isReprocessingAll}
                      >
                        {isReprocessingAll ? (
                          <>
                            <span className="import-modal__spinner import-modal__spinner--small"></span>
                            Reprocessando {errorCount} registro(s)...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-redo-alt"></i>
                            Reprocessar Todos com Erro ({errorCount})
                          </>
                        )}
                      </button>
                      <span className="import-modal__reprocess-all-hint">
                        Tenta importar novamente todos os registros com erro
                      </span>
                    </div>
                  )}

                  {/* Tabela de Resultados */}
                  <div className="import-modal__table-wrapper">
                    <table className="import-modal__table import-modal__table--results">
                      <thead>
                        <tr>
                          <th className="import-modal__th import-modal__th--num">Linha</th>
                          {config.columns.slice(0, 5).map((col, i) => (
                            <th key={i} className="import-modal__th">
                              {findColumnHeader(col)}
                            </th>
                          ))}
                          <th className="import-modal__th import-modal__th--status">Status</th>
                          <th className="import-modal__th import-modal__th--message">Detalhes</th>
                          <th className="import-modal__th import-modal__th--actions">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((result, rowIdx) => (
                          <React.Fragment key={rowIdx}>
                            <tr
                              className={`import-modal__tr ${
                                result.sucesso
                                  ? 'import-modal__tr--success'
                                  : 'import-modal__tr--error'
                              }`}
                            >
                              <td className="import-modal__td import-modal__td--num">{result.linha}</td>
                              {config.columns.slice(0, 5).map((col, colIdx) => (
                                <td key={colIdx} className="import-modal__td">
                                  {String(result.dados[col.field] ?? '')}
                                </td>
                              ))}
                              <td className="import-modal__td import-modal__td--status">
                                <span className={`import-modal__badge ${
                                  result.sucesso
                                    ? 'import-modal__badge--success'
                                    : 'import-modal__badge--error'
                                }`}>
                                  {result.sucesso ? (
                                    <><i className="fas fa-check-circle"></i> Sucesso</>
                                  ) : (
                                    <><i className="fas fa-exclamation-circle"></i> Erro</>
                                  )}
                                </span>
                              </td>
                              <td className="import-modal__td import-modal__td--message">
                                <span className={result.sucesso ? 'import-modal__msg--success' : 'import-modal__msg--error'}>
                                  {result.mensagem}
                                </span>
                              </td>
                              <td className="import-modal__td import-modal__td--actions">
                                {!result.sucesso && !isReprocessingAll && (
                                  <>
                                    {editingRow === rowIdx ? (
                                      <div className="import-modal__row-actions">
                                        <button
                                          className="import-modal__btn-row import-modal__btn-row--save"
                                          onClick={() => reprocessRow(rowIdx)}
                                          disabled={reprocessingRow === rowIdx}
                                          title="Reprocessar (ou pressione Enter)"
                                        >
                                          {reprocessingRow === rowIdx ? (
                                            <span className="import-modal__spinner import-modal__spinner--small"></span>
                                          ) : (
                                            <i className="fas fa-redo"></i>
                                          )}
                                          Salvar
                                        </button>
                                        <button
                                          className="import-modal__btn-row import-modal__btn-row--cancel"
                                          onClick={cancelEditing}
                                          title="Cancelar (ou pressione Esc)"
                                        >
                                          <i className="fas fa-times"></i>
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        className="import-modal__btn-row import-modal__btn-row--edit"
                                        onClick={() => startEditing(rowIdx)}
                                      >
                                        <i className="fas fa-edit"></i>
                                        Editar
                                      </button>
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                            {/* Linha expandida com formulário de edição de todos os campos */}
                            {editingRow === rowIdx && !result.sucesso && (
                              <tr className="import-modal__tr import-modal__tr--edit-row">
                                <td
                                  colSpan={config.columns.slice(0, 5).length + 4}
                                  className="import-modal__td import-modal__td--edit-form"
                                >
                                  <div className="import-modal__edit-form">
                                    <div className="import-modal__edit-form-title">
                                      <i className="fas fa-pencil-alt"></i>
                                      Editando linha {result.linha} — preencha os campos e pressione Enter ou clique em Salvar
                                    </div>
                                    <div className="import-modal__edit-form-grid">
                                      {config.columns.map((col) => (
                                        <div key={col.field} className="import-modal__edit-form-field">
                                          <label className="import-modal__edit-form-label">
                                            {col.required ? `${col.header} *` : col.header}
                                          </label>
                                          <input
                                            type="text"
                                            className="import-modal__edit-input"
                                            value={editData[col.field] ?? ''}
                                            onChange={(e) => handleEditChange(col.field, e.target.value)}
                                            onKeyDown={(e) => handleEditKeyDown(e, rowIdx)}
                                            placeholder={col.example || ''}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="import-modal__actions">
                    <button
                      className="import-modal__btn import-modal__btn--secondary"
                      onClick={handleClose}
                    >
                      Fechar
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default ImportModal;
